// hooks/useOrderStatus.ts
import { useState, useCallback, useEffect } from 'react'

/**
 * Custom hook for managing order status with auto-close functionality
 * Handles success messages with 30-second auto-close and manual close button
 */
export const useOrderStatus = () => {
  const [orderStatus, setOrderStatus] = useState<string | null>(null)
  const [orderStatusTimer, setOrderStatusTimer] = useState<NodeJS.Timeout | null>(null)

  // Function to close order status and clear timer
  const closeOrderStatus = useCallback(() => {
    setOrderStatus(null)
    if (orderStatusTimer) {
      clearTimeout(orderStatusTimer)
      setOrderStatusTimer(null)
    }
  }, [orderStatusTimer])

  // Function to set order status with auto-close timer for success messages
  const setOrderStatusWithTimer = useCallback((message: string) => {
    // Clear any existing timer
    if (orderStatusTimer) {
      clearTimeout(orderStatusTimer)
    }
    
    setOrderStatus(message)
    
    // Set auto-close timer for 30 seconds (only for success messages)
    if (message.toLowerCase().includes('success')) {
      const timer = setTimeout(() => {
        setOrderStatus(null)
        setOrderStatusTimer(null)
      }, 30000)
      
      setOrderStatusTimer(timer)
    }
  }, [orderStatusTimer])

  // Function to set immediate error status (no auto-close)
  const setOrderStatusError = useCallback((message: string) => {
    // Clear any existing timer
    if (orderStatusTimer) {
      clearTimeout(orderStatusTimer)
      setOrderStatusTimer(null)
    }
    
    setOrderStatus(message)
  }, [orderStatusTimer])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (orderStatusTimer) {
        clearTimeout(orderStatusTimer)
      }
    }
  }, [orderStatusTimer])

  return {
    orderStatus,
    setOrderStatus,
    setOrderStatusWithTimer,
    setOrderStatusError,
    closeOrderStatus
  }
}